// ────────────────────────────────────────────────────────────────────
// "What else this inspection recorded" — driven by the selected history row
// (design_handoff/README.md:198-217).
//
// The history table's own footer states this panel's reason for existing:
// THICKNESS IS ONE COLUMN OF AN INSPECTION, NOT THE WHOLE OF IT. Everything
// here is the rest of it — what was found and by which method, what was
// decided about each finding, the measurements that never produce a rate and
// still decide things, and the raw files the signed report was written from.
//
// WHICH INSPECTION IS OPEN is a DATE, never a position. `record.inspections`
// arrives newest first (`assemble.group_inspections`), and with no selection
// this panel opens on the CURRENT inspection — `order.currentEvent`, which
// answers from the dates. Four components read this off an array index before
// it was caught, and the fixtures agreed with them, so the suite stayed green
// on a screen naming the wrong inspection.
//
// WHAT THE RECORD CANNOT SUPPLY, THIS PANEL SAYS RATHER THAN COMPOSES:
//
//   • COVERAGE. The handoff's line reads "412 of 412 CMLs read · 100% of the
//     plan" — authored per inspection in the prototype. The record carries
//     which methods ran, not how much of the plan they covered, and there is
//     no plan-vs-read count anywhere behind it. A blank line there would read
//     as full coverage, so the line says what it does not know.
//   • FILE NAMES AND KINDS. `InspectionEvent.file_ids` is a list of ids and
//     nothing else. The cards are keyed by id and take their labels from a
//     caller that holds the file store, if one does; otherwise the id is what
//     is printed, because a filename invented here is a filename.
//
// MEASUREMENTS THAT ARE NOT THICKNESS has no writer at all — see ./emptyStates.
// ────────────────────────────────────────────────────────────────────
import type { CSSProperties } from "react";
import {
  MEASUREMENTS_NOT_CAPTURED, NONE_ON_THIS_INSPECTION,
} from "./emptyStates";
import { EMDASH } from "./format";
import { currentEvent, newestFirst } from "./order";
import type { AssetIntegrity, IntegrityFinding, InspectionEvent } from "./types";

/** A label for one raw file, when the caller holds the file store. The
 *  integrity record carries ids only. */
export interface FileLabel {
  id: number;
  name: string;
  kind: string;
}

const MICRO: CSSProperties = {
  fontSize: 8.5, fontWeight: 500, letterSpacing: ".14em", color: "var(--ink-4)",
};

/** Rows that are RECORDS, not inspections: the manufacturer's data report, the
 *  U-1 form, the line list. Nobody inspected anything, so an empty findings
 *  list is not a gap — it is the shape of a baseline read off a document.
 *  Matched on the source ref, which is how the handoff's prototype tells them
 *  apart (XZYNEXA.dc.html:2933). */
function isAsBuilt(ev: InspectionEvent): boolean {
  return /as-built|as built|U-1|line list/i.test(ev.source_ref);
}

function hasDetail(ev: InspectionEvent): boolean {
  return ev.findings.length > 0 || ev.observations.length > 0 || ev.file_ids.length > 0;
}

// ── findings ────────────────────────────────────────────────────────

type Tone = "danger" | "accent" | "plain";

/** How a disposition is written for a reader. The record's vocabulary is
 *  `promote_to_cml | repair | monitor | accept | refer_ffs`, and it is blank on
 *  a finding nobody has dispositioned yet — which is a state of its own and
 *  not an "accept". */
function dispositionLabel(d: string): string {
  switch (d) {
    case "repair":         return "Repair";
    case "refer_ffs":      return "Refer to fitness-for-service";
    case "monitor":        return "Monitor";
    case "promote_to_cml": return "Promote to a CML";
    case "accept":         return "Accept";
    case "":               return "no disposition recorded";
    default:               return d;
  }
}

/** The colour a disposition is printed in, from the record's own two
 *  controlled vocabularies — the engineer's DECISION first, and the finding's
 *  severity only where no decision has been recorded. Nothing here re-judges
 *  how bad a finding is; both fields were set by a person.
 *
 *  Repair and refer-to-FFS are the danger colour, monitor and promote are the
 *  accent, accept is plain ink (README.md:201). */
function toneFor(f: IntegrityFinding): Tone {
  switch (f.disposition) {
    case "repair": case "refer_ffs":         return "danger";
    case "monitor": case "promote_to_cml":   return "accent";
    case "accept":                           return "plain";
  }
  switch (f.severity) {
    case "crit":                return "danger";
    case "warn": case "watch":  return "accent";
    default:                    return "plain";
  }
}

const TONE_COLOUR: Record<Tone, string> = {
  danger: "var(--err)",
  accent: "var(--accent-deep)",
  plain:  "var(--ink-2)",
};

/** Findings grouped BY METHOD, which is what the section is headed. Each
 *  method keeps the order the record listed its findings in; the methods
 *  themselves keep the order they are first met in. Presentation only — no
 *  finding is re-ordered by how bad it is. */
function byMethod(findings: readonly IntegrityFinding[]): IntegrityFinding[] {
  const order: string[] = [];
  const groups = new Map<string, IntegrityFinding[]>();
  for (const f of findings) {
    const key = f.method || "";
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key)!.push(f);
  }
  return order.flatMap((k) => groups.get(k)!);
}

function FindingRow({ f }: { f: IntegrityFinding }): JSX.Element {
  const tone = toneFor(f);
  return (
    <div data-testid="finding-row" data-method={f.method}
         style={{
           display: "flex", alignItems: "flex-start", gap: 11,
           padding: "8px 0", borderBottom: "1px solid var(--line-3)",
         }}>
      <span className="font-mono"
            style={{
              fontSize: 9, fontWeight: 600, letterSpacing: ".08em",
              padding: "3px 7px", borderRadius: 5, flex: "none",
              width: 62, textAlign: "center",
              background: "var(--ink-1)", color: "var(--paper-2)",
            }}>
        {f.method || EMDASH}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 2 }}>
          {f.location || EMDASH}
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--ink-1)" }}>
          {f.detail}
        </div>
      </div>
      <span data-testid="finding-disposition" data-tone={tone}
            style={{
              fontSize: 11, fontWeight: 600, flex: "none", width: 158,
              textAlign: "right", color: TONE_COLOUR[tone],
            }}>
        {dispositionLabel(f.disposition)}
      </span>
    </div>
  );
}

// ── the panel ───────────────────────────────────────────────────────

export function InspectionDetailPanel({
  record, openedDate = null, onOpen, onOpenFile, fileLabels = [],
}: {
  record: AssetIntegrity;
  openedDate?: string | null;
  onOpen?: (date: string) => void;
  onOpenFile?: (fileId: number) => void;
  /** Names and kinds for `file_ids`, from whoever holds the file store. The
   *  integrity record does not carry them. */
  fileLabels?: readonly FileLabel[];
}): JSX.Element | null {
  const chips = newestFirst(record.inspections);
  // The open row: the one asked for, or the CURRENT inspection — the newest
  // DATE, never an array position.
  const open = chips.find((e) => e.date === openedDate) ?? currentEvent(record.inspections);
  if (!open) return null;

  const asBuilt = isAsBuilt(open);
  // Whether the ASSET carries any measurement at all decides which empty state
  // an empty inspection shows: "the system does not capture this yet" stops
  // being true the day a writer exists, and this panel must not have to be
  // edited on that day.
  const assetHasObservations = record.inspections.some((e) => e.observations.length > 0);
  const findings = byMethod(open.findings);
  const labels = new Map(fileLabels.map((f) => [f.id, f]));

  return (
    <section data-testid="inspection-detail"
             style={{
               border: "1px solid var(--hairline)", borderRadius: 10,
               background: "var(--paper-2)", padding: "14px 16px", marginTop: 16,
             }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14,
                    marginBottom: 11, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="font-mono" style={{ ...MICRO, marginBottom: 4 }}>
            WHAT ELSE THIS INSPECTION RECORDED
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
            <span data-testid="detail-date" style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink-1)" }}>
              {open.date}
            </span>
            <span data-testid="detail-ref" className="font-mono"
                  style={{ fontSize: 11, color: "var(--ink-3)" }}>
              {open.source_ref || EMDASH}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {chips.map((ev) => {
            const state = ev.date === open.date ? "open"
                        : hasDetail(ev) ? "has-detail" : "report-only";
            return (
              <span key={ev.date}
                    data-testid="detail-chip"
                    data-state={state}
                    onClick={() => onOpen?.(ev.date)}
                    className="font-mono"
                    style={{
                      fontSize: 10, fontWeight: 500, padding: "5px 9px", borderRadius: 6,
                      flex: "none", cursor: onOpen ? "pointer" : "default",
                      background: state === "open" ? "var(--ink-1)"
                                : state === "has-detail" ? "var(--line-3)" : "var(--surface-2)",
                      color: state === "open" ? "var(--paper-2)"
                           : state === "has-detail" ? "var(--ink-3)" : "var(--ink-4)",
                    }}>
                {ev.date}
              </span>
            );
          })}
        </div>
      </div>

      {asBuilt ? (
        <p data-testid="as-built-note"
           style={{ fontSize: 11.5, lineHeight: 1.65, color: "var(--ink-3)", padding: "2px 0 4px" }}>
          This row is not an inspection — it is the as-built record the baseline is read from.
          There are no findings because nobody inspected anything: the thickness came off the
          manufacturer&rsquo;s data report. It is held here because a corrosion rate needs a
          starting point, and a starting point taken from a document has to be labelled as one.
        </p>
      ) : (
        <>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            paddingBottom: 11, borderBottom: "1px solid var(--line-2)", marginBottom: 11,
          }}>
            <span data-testid="detail-coverage" className="font-mono"
                  style={{ fontSize: 10.5, fontWeight: 500, color: "var(--accent-deep)" }}>
              coverage is not on the record — these are the methods run, not how much of the
              plan they covered
            </span>
            {open.methods.length > 0 && (
              <span style={{ width: 1, height: 13, background: "var(--hairline)", flex: "none" }} />
            )}
            {open.methods.map((m) => (
              <span key={m} data-testid={`method-chip-${m}`} className="font-mono"
                    style={{
                      fontSize: 9.5, fontWeight: 500, padding: "3px 8px", borderRadius: 20,
                      background: "var(--line-2)", color: "var(--ink-2)", flex: "none",
                    }}>
                {m}
              </span>
            ))}
          </div>

          <div className="font-mono" style={{ ...MICRO, marginBottom: 7 }}>
            FINDINGS · BY METHOD, WITH WHAT WAS DECIDED ABOUT EACH
          </div>
          {findings.length > 0
            ? findings.map((f, i) => <FindingRow key={`${f.method}-${f.location}-${i}`} f={f} />)
            : (
              // Findings DO have a writer (server/routers/findings.py), so an
              // empty list here is an absence and reads as one.
              <div data-testid="findings-empty" className="font-mono"
                   style={{ fontSize: 10.5, color: "var(--ink-4)", padding: "8px 0" }}>
                {NONE_ON_THIS_INSPECTION}
              </div>
            )}

          <div className="font-mono" style={{ ...MICRO, margin: "13px 0 7px" }}>
            MEASUREMENTS THAT ARE NOT THICKNESS · THEY DO NOT PRODUCE A RATE, AND THEY STILL
            DECIDE THINGS
          </div>
          <div style={{
            display: "flex", flexWrap: "wrap", border: "1px solid var(--line-2)",
            borderRadius: 8, background: "var(--surface-2)", overflow: "hidden",
          }}>
            {open.observations.map((o, i) => (
              <div key={`${o.label}-${i}`}
                   style={{
                     flex: 1, minWidth: 120, padding: "10px 14px",
                     borderLeft: i ? "1px solid var(--line-2)" : undefined,
                   }}>
                <div className="font-mono"
                     style={{ fontSize: 8, fontWeight: 500, letterSpacing: ".12em",
                              color: "var(--ink-4)", marginBottom: 3, whiteSpace: "nowrap" }}>
                  {o.label}
                </div>
                <div className="font-mono" style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>
                  {o.value || EMDASH}
                </div>
                <div style={{ fontSize: 9.5, color: "var(--ink-4)", marginTop: 2 }}>{o.note}</div>
              </div>
            ))}
            {open.observations.length === 0 && (
              <div data-testid="measurements-empty"
                   style={{
                     fontSize: 11.5, lineHeight: 1.6, color: "var(--ink-4)", padding: "10px 14px",
                   }}>
                {assetHasObservations ? NONE_ON_THIS_INSPECTION : MEASUREMENTS_NOT_CAPTURED}
              </div>
            )}
          </div>

          <div className="font-mono" style={{ ...MICRO, margin: "13px 0 7px" }}>
            FILES HELD AGAINST IT · THE RAW DATA OUTLIVES THE REPORT
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {open.file_ids.map((id) => {
              const label = labels.get(id);
              return (
                // A real <button>, not a clickable div: OPEN RAW ▸ opens the
                // raw-file modal (./RawFileModal), and the modal hands focus
                // BACK to whatever held it when it opened. A div cannot hold
                // focus, so the caret would be dropped at the top of the
                // document every time a keyboard reader closed the modal.
                // Inner elements are spans for the same reason — <button>'s
                // content model is phrasing content, and a <div> inside one is
                // invalid markup that only happens to render.
                <button key={id} type="button" data-testid="file-card" data-file-id={id}
                        onClick={() => onOpenFile?.(id)}
                        style={{
                          border: "1px solid var(--hairline)", borderRadius: 7,
                          padding: "7px 11px", background: "var(--paper-2)",
                          minWidth: 158, textAlign: "left", font: "inherit",
                          cursor: onOpenFile ? "pointer" : "default",
                        }}>
                  <span className="font-mono"
                        style={{ display: "block", fontSize: 11, fontWeight: 500, color: "var(--ink-1)" }}>
                    {label?.name ?? `FILE ${id}`}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3 }}>
                    <span style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                      {/* The record carries the id and nothing else; a kind is
                          printed only when a caller supplied one. */}
                      {label?.kind ?? "kind not on the record"}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span className="font-mono"
                          style={{ fontSize: 8, fontWeight: 600, letterSpacing: ".1em",
                                   color: "var(--accent-deep)" }}>
                      OPEN RAW ▸
                    </span>
                  </span>
                </button>
              );
            })}
            {open.file_ids.length === 0 && (
              <div data-testid="files-empty" className="font-mono"
                   style={{ fontSize: 10.5, color: "var(--ink-4)", padding: "2px 0" }}>
                no raw file held against this inspection — only the signed report
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
